<template>
  <transition name="emp-dl-toast">
    <div
      v-if="visible"
      class="emp-dl-toast"
      :class="variant === 'error' ? 'emp-dl-toast--error' : 'emp-dl-toast--progress'"
      role="alert"
    >
      <div class="emp-dl-toast__inner">
        <div
          v-if="variant !== 'error'"
          class="emp-dl-toast__spinner"
          aria-hidden="true"
        ></div>
        <div v-else class="emp-dl-toast__err-icon" aria-hidden="true">!</div>
        <div class="emp-dl-toast__text">
          <div class="emp-dl-toast__title-row">
            <span class="emp-dl-toast__title-main">{{ title }}</span>
            <span
              v-if="contextLabel"
              class="emp-dl-toast__title-context"
              :title="contextLabel"
            >{{ contextLabel }}</span>
          </div>
          <div v-if="subtitle" class="emp-dl-toast__sub">{{ subtitle }}</div>
        </div>
        <button
          v-if="closable"
          type="button"
          class="emp-dl-toast__close"
          :title="closeLabel"
          @click="$emit('close')"
        >
          ×
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
/**
 * 全局可复用：文件下载 / 导出类「进行中」与「失败」右下角 Toast。
 * 已在 main.js 注册为 <EmpDownloadStatusToast />。
 *
 * Props: visible, variant ('progress'|'error'), title, subtitle?, contextLabel?（標題右側，過長省略）, closable?, closeLabel?
 * Events: @close
 *
 * 导出按钮浅灰/异常红样式见 emp-download-ui.scss（.emp-dl-export-btn*）
 */
export default {
  name: "EmpDownloadStatusToast",
  props: {
    visible: { type: Boolean, default: false },
    /** progress: 深色條 + 藍轉圈；error: 右下白底紅字 + 紅圓驚嘆號 */
    variant: {
      type: String,
      default: "progress",
      validator: (v) => ["progress", "error"].includes(v),
    },
    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    /** 標題同一行右側（如供應商名），過長由 CSS 省略號 */
    contextLabel: { type: String, default: "" },
    closable: { type: Boolean, default: true },
    closeLabel: { type: String, default: "Close" },
  },
};
</script>

<style scoped>
.emp-dl-toast {
  position: fixed;
  left: auto;
  bottom: 1rem;
  right: 1rem;
  top: auto;
  z-index: 10000;
  max-width: min(400px, calc(100vw - 2rem));
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.35;
}
.emp-dl-toast--progress {
  background: #1e1e2e;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #f4f4f5;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}
/* 導出失敗：淺粉底 + 紅框紅字，視口右下角（與需求示意一致） */
.emp-dl-toast--error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.1);
}
.emp-dl-toast__inner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px 10px 14px;
}
.emp-dl-toast__spinner {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border: 2px solid rgba(255, 255, 255, 0.25);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: emp-dl-toast-spin 0.7s linear infinite;
}
.emp-dl-toast__err-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border-radius: 50%;
  background: #dc2626;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
}
.emp-dl-toast__text {
  flex: 1;
  min-width: 0;
}
.emp-dl-toast__title-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}
.emp-dl-toast__title-main {
  flex-shrink: 0;
  font-weight: 600;
}
.emp-dl-toast__title-context {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  font-size: 12px;
  opacity: 0.92;
}
.emp-dl-toast--progress .emp-dl-toast__title-main {
  color: #ffffff;
}
.emp-dl-toast--progress .emp-dl-toast__title-context {
  color: #e4e4e7;
}
.emp-dl-toast--error .emp-dl-toast__title-main {
  color: #991b1b;
}
.emp-dl-toast--error .emp-dl-toast__title-context {
  color: #b91c1c;
}
.emp-dl-toast__sub {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 400;
}
.emp-dl-toast--progress .emp-dl-toast__sub {
  color: #a1a1aa;
}
.emp-dl-toast--error .emp-dl-toast__sub {
  color: #991b1b;
}
.emp-dl-toast__close {
  flex-shrink: 0;
  margin: -4px -6px 0 0;
  padding: 4px 8px;
  border: none;
  background: transparent;
  font-size: 18px;
  line-height: 1;
  color: inherit;
  opacity: 0.55;
  cursor: pointer;
  border-radius: 4px;
}
.emp-dl-toast--progress .emp-dl-toast__close {
  color: #e4e4e7;
  opacity: 0.7;
}
.emp-dl-toast--error .emp-dl-toast__close {
  color: #991b1b;
}
.emp-dl-toast__close:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.06);
}
.emp-dl-toast--progress .emp-dl-toast__close:hover {
  background: rgba(255, 255, 255, 0.12);
}
.emp-dl-toast--error .emp-dl-toast__close:hover {
  background: rgba(220, 38, 38, 0.08);
}

@keyframes emp-dl-toast-spin {
  to {
    transform: rotate(360deg);
  }
}

.emp-dl-toast-enter-active,
.emp-dl-toast-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.emp-dl-toast-enter,
.emp-dl-toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
